import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/services/api";

// Priceable usage event types, in display order, with friendly labels.
const PRICEABLE = [
  { key: "envelope_sent", label: "Envelope sent" },
  { key: "envelope_completed", label: "Envelope completed" },
  { key: "sms_sent", label: "SMS sent" },
  { key: "email_sent", label: "Email sent" },
  { key: "api_call", label: "API call (overage)" },
  { key: "storage_used", label: "Storage (per byte)" },
  { key: "template_created", label: "Template created" },
  { key: "bulk_send", label: "Bulk send" },
  { key: "signing_link_created", label: "Signing link" },
  { key: "in_person_session", label: "In-person session" },
];

/**
 * Admin Pricing page: sets the platform-wide DEFAULT usage prices (manual /
 * invoice billing). These apply to every org unless the org has its own
 * override (set per org in Billing → Settings). Plan prices are managed in
 * Stripe and are not editable here.
 */
const AdminPricing = () => {
  const queryClient = useQueryClient();
  const [values, setValues] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "platform-usage-prices"],
    queryFn: async () => {
      const response = await api.get("/admin/pricing/usage-defaults");
      return response.data?.data;
    },
  });

  useEffect(() => {
    if (data?.prices) {
      const seeded = {};
      for (const { key } of PRICEABLE) {
        seeded[key] = String(data.prices[key] ?? 0);
      }
      setValues(seeded);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async (prices) => {
      const response = await api.put("/admin/pricing/usage-defaults", {
        prices,
      });
      return response.data?.data;
    },
    onSuccess: () => {
      toast.success("Default usage prices updated");
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-usage-prices"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "usage-log"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "unbilled-usage"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "usage-prices"] });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.error?.message ||
          "Failed to update default usage prices",
      );
    },
  });

  const handleSave = () => {
    const prices = {};
    for (const { key } of PRICEABLE) {
      const num = Number(values[key]);
      if (!Number.isFinite(num) || num < 0) {
        toast.error(`Invalid price for ${key}`);
        return;
      }
      prices[key] = num;
    }
    mutation.mutate(prices);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Pricing</h1>
        <p className="text-muted-foreground">
          Platform default usage prices for manual (invoice) billing.
        </p>
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle>Default usage prices</CardTitle>
          <CardDescription>
            Applied to every organisation unless it has a per-org override
            (Organisations → Billing → Settings). Saving re-rates all unbilled
            usage rows for orgs on the default (billed rows keep their invoiced
            price). Plan prices are managed in Stripe and not editable here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {PRICEABLE.map(({ key, label }) => (
                  <div key={key} className="space-y-1.5">
                    <Label htmlFor={`pp-${key}`} className="text-sm">
                      {label}
                    </Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <Input
                        id={`pp-${key}`}
                        type="number"
                        min="0"
                        step="0.001"
                        className="pl-7 font-mono"
                        value={values[key] ?? ""}
                        onChange={(e) =>
                          setValues((v) => ({ ...v, [key]: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-end">
                <Button onClick={handleSave} disabled={mutation.isPending}>
                  {mutation.isPending ? "Saving…" : "Save default prices"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPricing;
